import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { loadGoogleMapsScript } from "../../../../../utils/googleMapsLoader";
import { getGeocodingByCoordinates } from "../../../../../services/geocodingService";
import { getCachedData, setCachedData } from "../../../../../services/cacheService";
import { logGeocodingMap } from "../../../../../utils/geocodingMapDebug";
import { pickCityAndCountryFromAddressComponents } from "../../../../../utils/nominatimGoogleAddressFormat";
import "./autocompleteinput.scss";

const language = localStorage.getItem("i18nextLng") || "he";

const AutoCompleteInput = ({
  saveLocationEdit,
  location,
  saveLocationSelection,
  closeInput,
}) => {
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const autocompleteSessionTokenRef = useRef(null);
  const [inputValue, setInputValue] = useState(location?.fullname || "");
  const [isLoaded, setIsLoaded] = useState(false);
  const [predictions, setPredictions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Enrich place with city/country via backend reverse geocode (English labels for backend).
  const translatePlaceToEn = useCallback(async (place) => {
    if (!place.latitude || !place.longitude) {
      console.error("[AutoCompleteInput] Missing coordinates for translation");
      return;
    }

    try {
      const result = await getGeocodingByCoordinates(place.latitude, place.longitude, "en");

      if (result.ok && result.status === "success" && result.data) {
        const components = result.data.address_components;
        const picked = pickCityAndCountryFromAddressComponents(components);
        logGeocodingMap("autocomplete_reverse_geocode_pick", {
          lat: place.latitude,
          lng: place.longitude,
          address_components: components,
          parsedCity: picked.city,
          parsedCountry: picked.country,
        });
        if (picked.country) {
          place.country = picked.country;
        }
        if (picked.city) {
          place.city = picked.city;
        } else if (
          Array.isArray(place.types) &&
          place.types.includes("locality") &&
          typeof place.name === "string" &&
          place.name.trim()
        ) {
          place.city = place.name.trim();
        }
        saveLocationEdit(false);
        saveLocationSelection(place);
        return;
      }

      if (result.status === "not_found") {
        console.warn(
          "[AutoCompleteInput] Reverse geocoding: not_found (no address for coordinates).",
          result.error
        );
        saveLocationEdit(false);
        saveLocationSelection(place);
        return;
      }

      if (result.status === "bad_request") {
        console.warn("[AutoCompleteInput] Reverse geocoding: bad_request.", result.error);
        saveLocationEdit(false);
        saveLocationSelection(place);
        return;
      }

      if (result.status === "network_error") {
        console.error("[AutoCompleteInput] Reverse geocoding: network_error.", result.error);
        saveLocationEdit(false);
        saveLocationSelection(place);
        return;
      }

      if (result.status === "provider_error") {
        console.error("[AutoCompleteInput] Reverse geocoding: provider_error.", result.error);
        saveLocationEdit(false);
        saveLocationSelection(place);
        return;
      }

      if (result.status === "rate_limited") {
        console.error("[AutoCompleteInput] Reverse geocoding: rate_limited.", result.error);
        saveLocationEdit(false);
        saveLocationSelection(place);
        return;
      }

      console.error("[AutoCompleteInput] Reverse geocoding: unexpected envelope.", result);
      saveLocationEdit(false);
      saveLocationSelection(place);
    } catch (error) {
      console.error("[AutoCompleteInput] Reverse geocoding: unexpected exception.", error);
      saveLocationEdit(false);
      saveLocationSelection(place);
    }
  }, [saveLocationEdit, saveLocationSelection]);

  // Process selected place
  const selectPlace = useCallback(async (place) => {
    if (!place || !place.location) {
      saveLocationSelection("");
      return;
    }

    try {
      const loc = place.location;
      place.latitude = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
      place.longitude = typeof loc.lng === 'function' ? loc.lng() : loc.lng;

      const types = place.types || [];
      const displayName = place.displayName || "";
      const formattedAddress = place.formattedAddress || "";

      if (types.includes("lodging")) {
        place.fullname = displayName || formattedAddress;
        place.hotelserched = displayName || formattedAddress;
        place.name = displayName;
        place.formatted_address = formattedAddress;
      } else if (types.includes("locality")) {
        place.fullname = formattedAddress || displayName;
        place.hotelserched = false;
        place.name = displayName;
        place.formatted_address = formattedAddress;
      } else {
        place.fullname = displayName || formattedAddress;
        place.hotelserched = false;
        place.name = displayName;
        place.formatted_address = formattedAddress;
      }

      // Update input value
      const valueToSet = place.fullname || place.name || place.formatted_address || "";
      setInputValue(valueToSet);
      if (inputRef.current) {
        inputRef.current.value = valueToSet;
      }
      setShowDropdown(false);

      await translatePlaceToEn(place);
    } catch (error) {
      console.error("[AutoCompleteInput] Error in selectPlace:", error);
      saveLocationSelection("");
    }
  }, [saveLocationSelection, translatePlaceToEn]);

  // Load Google Maps API
  useEffect(() => {
    let isMounted = true;

    const loadMaps = async () => {
      try {
        await loadGoogleMapsScript({
          libraries: ["places"],
          language: language,
        });
        if (isMounted) {
          setIsLoaded(true);
        }
      } catch (error) {
        console.error("[AutoCompleteInput] Error loading Google Maps API:", error);
        if (isMounted) {
          setIsLoaded(false);
        }
      }
    };

    loadMaps();

    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch autocomplete predictions using Google Places API (New)
  const fetchPredictions = useCallback(async (input) => {
    if (!input || input.length < 2 || !window.google?.maps) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    // Prevent any other autocomplete services from interfering
    // Remove any Photon dropdowns that might appear
    const photonDropdowns = document.querySelectorAll('.photon-autocomplete-dropdown');
    photonDropdowns.forEach(dropdown => dropdown.remove());

    try {
      console.log("[AutoCompleteInput] Fetching predictions using Google Places API (New) for:", input);
      
      // Import AutocompleteSessionToken and AutocompleteSuggestion (New API)
      const { AutocompleteSessionToken, AutocompleteSuggestion } = await window.google.maps.importLibrary("places");
      
      // Create session token if not exists
      if (!autocompleteSessionTokenRef.current) {
        autocompleteSessionTokenRef.current = new AutocompleteSessionToken();
        console.log("[AutoCompleteInput] Created new session token");
      }

      // Use Google Places API (New) - AutocompleteSuggestion
      const request = {
        input: input,
        sessionToken: autocompleteSessionTokenRef.current,
        language: language,
        includedRegionCodes: [], // Empty means all regions
        includedPrimaryTypes: ['locality', 'administrative_area_level_1', 'country', 'establishment', 'lodging'],
      };

      console.log("[AutoCompleteInput] Fetching suggestions with request:", request);
      const result = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      console.log("[AutoCompleteInput] Received result:", result);
      
      const suggestions = result?.suggestions || [];
      
      if (suggestions && suggestions.length > 0) {
        // Convert suggestions to predictions format for compatibility
        const predictions = suggestions
          .map(suggestion => {
            const placePrediction = suggestion.placePrediction;
            if (!placePrediction || !placePrediction.placeId) {
              return null;
            }
            
            // Log the structure to understand it better
            if (suggestions.indexOf(suggestion) === 0) {
              console.log("[AutoCompleteInput] Sample placePrediction structure:", placePrediction);
            }
            
            // Extract main text and secondary text from placePrediction
            // In the new API, text structure might be different
            let mainText = '';
            let secondaryText = '';
            
            if (placePrediction.text) {
              // Try different possible structures
              if (typeof placePrediction.text === 'string') {
                mainText = placePrediction.text;
              } else if (placePrediction.text.text) {
                mainText = placePrediction.text.text;
              } else if (placePrediction.text.primaryText) {
                mainText = placePrediction.text.primaryText;
                secondaryText = placePrediction.text.secondaryText || '';
              }
            }
            
            // Fallback to placeId if no text found
            if (!mainText) {
              mainText = placePrediction.placeId;
            }
            
            return {
              place_id: placePrediction.placeId,
              description: mainText,
              structured_formatting: {
                main_text: mainText,
                secondary_text: secondaryText,
              },
              placePrediction: placePrediction,
            };
          })
          .filter(p => p !== null && p.place_id);
        
        console.log("[AutoCompleteInput] Converted predictions:", predictions);
        console.log("[AutoCompleteInput] Setting predictions and showing dropdown");
        setPredictions(predictions);
        // Calculate dropdown position for portal
        if (inputRef.current) {
          const rect = inputRef.current.getBoundingClientRect();
          const isMobile = window.innerWidth <= 768;
          const isSmallMobile = window.innerWidth <= 480;
          
          let dropdownWidth;
          let left;
          
          if (isSmallMobile) {
            // On small mobile: full width minus padding
            dropdownWidth = window.innerWidth - 24;
            left = 12;
          } else if (isMobile) {
            // On mobile: full width minus padding
            dropdownWidth = window.innerWidth - 32;
            left = 16;
          } else {
            // On desktop: wider than input, minimum 400px
            dropdownWidth = Math.max(rect.width * 1.5, 400);
            // Ensure dropdown doesn't go off screen
            const maxLeft = window.innerWidth - dropdownWidth - 16;
            left = Math.min(rect.left + window.scrollX, maxLeft);
            left = Math.max(left, 16); // Minimum left padding
          }
          
          setDropdownPosition({
            top: rect.bottom + window.scrollY + 6,
            left: left,
            width: dropdownWidth,
          });
        }
        setShowDropdown(true);
        setSelectedIndex(-1);
        console.log("[AutoCompleteInput] showDropdown set to true, predictions count:", predictions.length);
      } else {
        console.warn("[AutoCompleteInput] No suggestions received");
        setPredictions([]);
        setShowDropdown(false);
      }
    } catch (error) {
      console.error("[AutoCompleteInput] Error fetching predictions:", error);
      setPredictions([]);
      setShowDropdown(false);
    }
  }, [language]);

  // Handle input change
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setInputValue(value);
    
    // Immediately remove any Photon dropdowns that might appear
    setTimeout(() => {
      const photonDropdowns = document.querySelectorAll('.photon-autocomplete-dropdown');
      photonDropdowns.forEach(dropdown => {
        console.log("[AutoCompleteInput] Removing Photon dropdown on input change:", dropdown);
        dropdown.remove();
      });
    }, 0);
    
    fetchPredictions(value);
  }, [fetchPredictions]);

  // Handle prediction selection
  const handlePredictionSelect = useCallback(async (prediction) => {
    const placeId = prediction.place_id || prediction.placePrediction?.placeId || prediction.placePrediction?.place?.id;
    
    if (!placeId) {
      console.error("[AutoCompleteInput] No place ID in prediction:", prediction);
      return;
    }

    try {
      console.log("[AutoCompleteInput] Selecting prediction with placeId:", placeId);
      
      // Check cache
      const CACHE_TTL = 24 * 60 * 60 * 1000;
      const cacheKey = `place_details_${placeId}`;
      const cachedPlaceData = getCachedData(cacheKey, CACHE_TTL);

      let place;
      if (cachedPlaceData) {
        console.log("[AutoCompleteInput] Using cached place data");
        place = {
          id: cachedPlaceData.id,
          displayName: cachedPlaceData.displayName,
          formattedAddress: cachedPlaceData.formattedAddress,
          location: {
            lat: () => cachedPlaceData.location.lat,
            lng: () => cachedPlaceData.location.lng,
          },
          types: cachedPlaceData.types || [],
        };
      } else {
        console.log("[AutoCompleteInput] Fetching place details");
        // Fetch place details using new API
        // ⚠️ DO NOT use Legacy API: PlacesService.getDetails() or place.getPlace()
        // ✅ Using New API: Place.fetchFields()
        const { Place } = await window.google.maps.importLibrary("places");
        place = new Place({ id: placeId });
        
        await place.fetchFields({
          fields: ["id", "displayName", "formattedAddress", "location", "types"],
        });
        
        console.log("[AutoCompleteInput] Place fetched:", place);
        
        // Cache place data
        const placeData = {
          id: place.id,
          displayName: place.displayName,
          formattedAddress: place.formattedAddress,
          location: {
            lat: typeof place.location.lat === 'function' ? place.location.lat() : place.location.lat,
            lng: typeof place.location.lng === 'function' ? place.location.lng() : place.location.lng,
          },
          types: place.types || [],
        };
        setCachedData(cacheKey, placeData, CACHE_TTL);
      }

      // Create new session token for next search
      const { AutocompleteSessionToken } = await window.google.maps.importLibrary("places");
      autocompleteSessionTokenRef.current = new AutocompleteSessionToken();

      await selectPlace(place);
    } catch (error) {
      console.error("[AutoCompleteInput] Error selecting prediction:", error);
    }
  }, [selectPlace]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!showDropdown || predictions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev < predictions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      handlePredictionSelect(predictions[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
      setSelectedIndex(-1);
    }
  }, [showDropdown, predictions, selectedIndex, handlePredictionSelect]);

  // Update input value when location prop changes
  useEffect(() => {
    if (location?.fullname) {
      const value = location.fullname;
      setInputValue(value);
      if (inputRef.current) {
        inputRef.current.value = value;
      }
    }
  }, [location?.fullname]);

  // Update dropdown position on scroll/resize
  useEffect(() => {
    if (!showDropdown || !inputRef.current) return;

    const updatePosition = () => {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        const isMobile = window.innerWidth <= 768;
        const isSmallMobile = window.innerWidth <= 480;
        
        let dropdownWidth;
        let left;
        
        if (isSmallMobile) {
          // On small mobile: full width minus padding
          dropdownWidth = window.innerWidth - 24;
          left = 12;
        } else if (isMobile) {
          // On mobile: full width minus padding
          dropdownWidth = window.innerWidth - 32;
          left = 16;
        } else {
          // On desktop: wider than input, minimum 400px
          dropdownWidth = Math.max(rect.width * 1.5, 400);
          // Ensure dropdown doesn't go off screen
          const maxLeft = window.innerWidth - dropdownWidth - 16;
          left = Math.min(rect.left + window.scrollX, maxLeft);
          left = Math.max(left, 16); // Minimum left padding
        }
        
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 6,
          left: left,
          width: dropdownWidth,
        });
      }
    };

    // Update position immediately
    updatePosition();

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showDropdown]);

  // Remove any Photon autocomplete dropdowns that might appear
  useEffect(() => {
    const removePhotonDropdowns = () => {
      const photonDropdowns = document.querySelectorAll('.photon-autocomplete-dropdown');
      photonDropdowns.forEach(dropdown => {
        console.log("[AutoCompleteInput] Removing Photon dropdown:", dropdown);
        dropdown.remove();
      });
      
      // Also check for any other potential Photon-related elements
      const photonElements = document.querySelectorAll('[class*="photon"], [id*="photon"]');
      photonElements.forEach(el => {
        if (el.classList.contains('photon-autocomplete-dropdown') || 
            el.id.includes('photon') || 
            el.className.includes('photon-autocomplete')) {
          console.log("[AutoCompleteInput] Removing Photon element:", el);
          el.remove();
        }
      });
    };

    // Remove immediately and set up observer with more aggressive monitoring
    removePhotonDropdowns();

    const observer = new MutationObserver((mutations) => {
      let shouldRemove = false;
      mutations.forEach(mutation => {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1) { // Element node
            if (node.classList?.contains('photon-autocomplete-dropdown') ||
                node.id?.includes('photon') ||
                node.className?.includes('photon-autocomplete')) {
              shouldRemove = true;
            }
            // Check children too
            if (node.querySelectorAll) {
              const photonChildren = node.querySelectorAll('.photon-autocomplete-dropdown, [class*="photon"], [id*="photon"]');
              if (photonChildren.length > 0) {
                shouldRemove = true;
              }
            }
          }
        });
      });
      
      if (shouldRemove) {
        removePhotonDropdowns();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'id']
    });

    // Also set up interval check as backup
    const intervalId = setInterval(removePhotonDropdowns, 100);

    return () => {
      observer.disconnect();
      clearInterval(intervalId);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        const dropdown = document.querySelector('.autocomplete-dropdown');
        if (dropdown && !dropdown.contains(event.target)) {
          setShowDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Debug render
  console.log("[AutoCompleteInput] Render - showDropdown:", showDropdown, "predictions.length:", predictions.length);

  // Render dropdown via Portal to body to avoid stacking context issues
  const dropdownContent = showDropdown && predictions.length > 0 && (
    <div 
      className="autocomplete-dropdown"
      style={{
        position: 'fixed',
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        width: `${dropdownPosition.width}px`,
        zIndex: 100000100,
      }}
    >
      {predictions.map((prediction, index) => (
        <div
          key={prediction.place_id}
          className={`autocomplete-item ${index === selectedIndex ? 'selected' : ''}`}
          onClick={() => handlePredictionSelect(prediction)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <div className="autocomplete-item-main-text">
            {prediction.structured_formatting.main_text}
          </div>
          <div className="autocomplete-item-secondary-text">
            {prediction.structured_formatting.secondary_text}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <div className="autoComplete-input-box-wrapper">
        <div ref={containerRef} className="autoComplete-input-box-container">
          <input
            ref={inputRef}
            type="text"
            placeholder="הקלד מלון עיר או יעד"
            className="autoComplete-input-box"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (inputValue && inputValue.length >= 2) {
                fetchPredictions(inputValue);
              }
            }}
            disabled={!isLoaded}
            required
          />
        </div>
      </div>
      
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  );
};

export default AutoCompleteInput;
